import React, { useState, useEffect } from 'react';
import { subscribeRoom, leaveRoom } from '../shared/roomTx';
import { FL_ROOM_STATUS, FL_GAME_MODE } from '../shared/fiveLeague';
import { CyberBackground, NeonText, CyberButton, GlowingBorder } from '../components/CyberEffects';
import './ChainResult.css';

function ChainResult({ navigate, playerId, roomId }) {
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);

  // ルーム購読
  useEffect(() => {
    if (!roomId) {
      navigate('home');
      return;
    }

    const unsub = subscribeRoom(roomId, (data) => {
      setLoading(false);
      setRoom(data);
    });

    return () => unsub();
  }, [roomId, navigate]);

  const handlePlayAgain = () => {
    // 待機画面に戻る（同じルームで再プレイ）
    navigate('wait', { roomId });
  };

  const handleLeave = async () => {
    await leaveRoom(roomId, playerId);
    navigate('home');
  };

  if (loading) {
    return (
      <div className="chain-result-container">
        <CyberBackground showParticles={true} />
        <div className="loading-spinner">読み込み中...</div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="chain-result-container">
        <CyberBackground showParticles={true} />
        <p className="error-message">ルームが見つかりません</p>
        <button className="btn-secondary" onClick={() => navigate('home')}>
          ホームに戻る
        </button>
      </div>
    );
  }

  const game = room.game;
  if (!game) {
    return (
      <div className="chain-result-container">
        <CyberBackground showParticles={true} />
        <p className="error-message">ゲームデータがありません</p>
        <button className="btn-secondary" onClick={() => navigate('home')}>
          ホームに戻る
        </button>
      </div>
    );
  }

  const isPerfect = game.correctCount === game.answerCount;
  const controllers = room.players?.filter(p => p.role !== 'display') || [];

  return (
    <div className="chain-result-container animate-fade-in">
      <CyberBackground showParticles={true} />

      <div className="chain-result-content">
        {/* 結果ヘッダー */}
        <div className="result-header">
          <div className={`result-icon ${isPerfect ? 'perfect' : 'gameover'}`}>
            {isPerfect ? '🎉' : '💥'}
          </div>
          <h1 className="result-title">
            <NeonText color={isPerfect ? 'accent' : 'pink'} size="xl">
              {isPerfect ? 'PERFECT!' : 'GAME OVER'}
            </NeonText>
          </h1>
        </div>

        {/* スコア */}
        <div className="result-score">
          <GlowingBorder color={isPerfect ? 'accent' : 'blue'} className="score-box">
            <span className="score-current">{game.correctCount}</span>
            <span className="score-separator">/</span>
            <span className="score-total">{game.answerCount}</span>
            <span className="score-label">正解</span>
          </GlowingBorder>
        </div>

        {/* 回答リスト */}
        <div className="answers-section">
          <h2 className="answers-title">
            <NeonText color="blue" size="sm">回答一覧</NeonText>
          </h2>
          <div className="answers-list">
            {game.submittedAnswers?.map((answer, i) => {
              const player = room.players.find(p =>
                room.playerOrder[answer.playerSlot] === p.playerId
              );
              return (
                <div key={i} className="answer-item">
                  <span className="answer-num">{i + 1}</span>
                  <span className="answer-text">{answer.text}</span>
                  <span className="answer-player">{player?.nickname || '???'}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* 残りの正解（タイムアウト時） */}
        {!isPerfect && game.answersForDisplay && (
          <div className="remaining-section">
            <h2 className="remaining-title">
              <NeonText color="pink" size="sm">残りの答え</NeonText>
            </h2>
            <div className="remaining-list">
              {game.answersForDisplay
                .filter(ans => !game.submittedAnswers?.some(s => s.reading === ans.reading))
                .slice(0, game.answerCount - game.correctCount)
                .map((answer, i) => (
                  <div key={i} className="remaining-item">
                    {answer.text}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* アクションボタン */}
        <div className="result-actions">
          <CyberButton
            variant="primary"
            className="play-again-btn"
            onClick={handlePlayAgain}
          >
            もう一度遊ぶ
          </CyberButton>
          <button
            className="cyber-btn-secondary leave-btn"
            onClick={handleLeave}
          >
            終了する
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChainResult;
